import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { StepProps } from '@/widgets/ServiceFormTemplate';
import { FormBlock } from '@/widgets/ServiceFormTemplate/ui/FormBlock';
import { SelectableList } from '@/features/ui/SelectableList';
import { ConditionView, Grid, StepControlPanel, Loader } from '@/shared/ui';

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import ServiceAvailabilityContext, { Reason } from '@/contexts/ServiceAvailability/ServiceAvailabilityContext';
import { ServiceSupportedCustomWeaponList, Weapon, FormDataType } from '@/api/types';

import { MultipleWeaponSelectionSchema } from './model/types';
import { loadWeaponDataFn, renderWeapon } from '@/features/ui/Steps/utils';
import { NpuFormBlock } from '@/features/ui/NpuFormBlock/NpuFormBlock';
import './MultipleWeaponSelection.scss';

export interface MultipleWeaponSelectionProps {
  onDraftCreate?: (data?: { stateForm: FormDataType }) => Promise<string>;
}

export const MultipleWeaponSelection: React.FC<StepProps<MultipleWeaponSelectionProps>> = ({
  next,
  serviceCode,
  onDraftCreate,
  currentRequestId,
}) => {
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<MultipleWeaponSelectionSchema>();
  const { disableCause } = useContext(ServiceAvailabilityContext);
  const {
    data: responseData,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: queryKeys.services.weapons(serviceCode as string),
    queryFn: () => loadWeaponDataFn(serviceCode as ServiceSupportedCustomWeaponList),
    enabled: !!serviceCode,
  });

  const { weapons, orgNpuOptions } = responseData ?? {
    weapons: [],
    orgNpuOptions: [],
  };

  useEffect(() => {
    if (weapons.length > 0) {
      setValue('weapon', weapons);
    }
  }, [weapons, setValue]);

  useEffect(() => {
    if (isSuccess && !isLoading && isEmpty(weapons)) {
      disableCause({ reason: Reason.NOT_ENOUGH_DATA });
    }
  }, [isSuccess, isLoading, weapons, disableCause]);

  const handleNextStep = handleSubmit(async () => {
    if (!currentRequestId && onDraftCreate) {
      await onDraftCreate({ stateForm: getValues() as FormDataType });
    }
    next();
  });

  return (
    <ConditionView if={!isLoading} else={<Loader />}>
      <FormBlock title="Виберіть зброю">
        <Controller
          name="weapon"
          control={control}
          render={() => (
            <SelectableList<Weapon>
              inputType="multiple_readonly"
              items={weapons}
              getItemId={(weapon) => weapon.id}
              renderItem={(weapon) => <div className="weapon-list__weapon">{renderWeapon(weapon)}</div>}
              containerClassName="weapon-list"
              itemClassName="weapon-list__item"
              errorMessage={errors.weapon?.message}
            />
          )}
        />
      </FormBlock>
      <NpuFormBlock orgNpuOptions={orgNpuOptions} />
      <StepControlPanel next={handleNextStep} />
    </ConditionView>
  );
};

export const MultipleWeaponSelectionPreview: React.FC = () => {
  const { getValues } = useFormContext<MultipleWeaponSelectionSchema>();
  const { orgNpu, weapon } = getValues();
  const weaponsData = weapon ?? [];
  const orgNpuPreview: Array<[string, Nullable<string>]> = [['Орган НПУ, до якого надсилається заявка', orgNpu?.label]];

  const allWeaponsPreview: Array<[string, Nullable<string>]> = weaponsData.map((weaponData) => {
    const weaponModel = (
      weaponData.weaponType === 'firearm_part' ? (weaponData.ORNM ?? null) : (weaponData.ORMD ?? null)
    ) as Nullable<string>;
    const serial = weaponData?.ORSR ?? null;
    const value = weaponModel ? `${weaponModel}<br />${serial ?? ''}` : null;
    return ['Модель, серія та номер', value];
  });

  return (
    <>
      <Grid title="Вибрана зброя" data={allWeaponsPreview} isHtmlData={true} />
      <Grid title="Орган НПУ" data={orgNpuPreview} />
    </>
  );
};
